import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  addDoc,
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const SOCIAL_MEDIA_COLLECTION = 'socialMediaData';

/**
 * Get social media data for a specific user
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} Social media data object
 */
export const getUserSocialMediaData = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const userDocRef = doc(db, SOCIAL_MEDIA_COLLECTION, userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const data = userDoc.data();
      return {
        id: userDoc.id,
        ...data,
        // Convert Firestore Timestamps to JavaScript Dates
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null
      };
    } else {
      // Return default structure if no data exists
      return {
        id: userId,
        socialData: {
          facebook: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
          instagram: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
          tiktok: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 },
          youtube: { followers: 0, views: 0, likes: 0, comments: 0, shares: 0, leads: 0, engagement: 0 }
        },
        platformEntries: {
          facebook: [],
          instagram: [],
          tiktok: [],
          youtube: []
        },
        managementRequest: null,
        createdAt: null,
        updatedAt: null
      };
    }
  } catch (error) {
    console.error('Error getting user social media data:', error);
    throw error;
  }
};

/**
 * Save social media data for a specific user
 * @param {string} userId - The user's UID
 * @param {Object} socialData - Social media data object
 * @param {Object} platformEntries - Platform entries data
 * @returns {Promise<Object>} Updated social media data
 */
export const saveUserSocialMediaData = async (userId, socialData, platformEntries) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    const userDocRef = doc(db, SOCIAL_MEDIA_COLLECTION, userId);
    
    // Check if document exists to determine if we're creating or updating
    const existingDoc = await getDoc(userDocRef);
    const isUpdate = existingDoc.exists();

    const dataToSave = {
      socialData: {
        facebook: {
          followers: Number(socialData.facebook?.followers) || 0,
          views: Number(socialData.facebook?.views) || 0,
          likes: Number(socialData.facebook?.likes) || 0,
          comments: Number(socialData.facebook?.comments) || 0,
          shares: Number(socialData.facebook?.shares) || 0,
          leads: Number(socialData.facebook?.leads) || 0,
          engagement: Number(socialData.facebook?.engagement) || 0
        },
        instagram: {
          followers: Number(socialData.instagram?.followers) || 0,
          views: Number(socialData.instagram?.views) || 0,
          likes: Number(socialData.instagram?.likes) || 0,
          comments: Number(socialData.instagram?.comments) || 0,
          shares: Number(socialData.instagram?.shares) || 0,
          leads: Number(socialData.instagram?.leads) || 0,
          engagement: Number(socialData.instagram?.engagement) || 0
        },
        tiktok: {
          followers: Number(socialData.tiktok?.followers) || 0,
          views: Number(socialData.tiktok?.views) || 0,
          likes: Number(socialData.tiktok?.likes) || 0,
          comments: Number(socialData.tiktok?.comments) || 0,
          shares: Number(socialData.tiktok?.shares) || 0,
          leads: Number(socialData.tiktok?.leads) || 0,
          engagement: Number(socialData.tiktok?.engagement) || 0
        },
        youtube: {
          followers: Number(socialData.youtube?.followers) || 0,
          views: Number(socialData.youtube?.views) || 0,
          likes: Number(socialData.youtube?.likes) || 0,
          comments: Number(socialData.youtube?.comments) || 0,
          shares: Number(socialData.youtube?.shares) || 0,
          leads: Number(socialData.youtube?.leads) || 0,
          engagement: Number(socialData.youtube?.engagement) || 0
        }
      },
      platformEntries: {
        facebook: Array.isArray(platformEntries.facebook) ? platformEntries.facebook : [],
        instagram: Array.isArray(platformEntries.instagram) ? platformEntries.instagram : [],
        tiktok: Array.isArray(platformEntries.tiktok) ? platformEntries.tiktok : [],
        youtube: Array.isArray(platformEntries.youtube) ? platformEntries.youtube : []
      },
      updatedAt: serverTimestamp()
    };

    // Add createdAt only for new documents
    if (!isUpdate) {
      dataToSave.createdAt = serverTimestamp();
    }

    await setDoc(userDocRef, dataToSave);

    // Return the saved data with proper timestamp conversion
    return {
      id: userId,
      ...dataToSave,
      createdAt: isUpdate ? existingDoc.data().createdAt?.toDate() : new Date(),
      updatedAt: new Date()
    };
  } catch (error) {
    console.error('Error saving user social media data:', error);
    throw error;
  }
};

/**
 * Add a new platform entry for a specific user
 * @param {string} userId - The user's UID
 * @param {string} platform - Platform name (facebook, instagram, tiktok, youtube)
 * @param {Object} entryData - Entry data object
 * @returns {Promise<Object>} Updated social media data
 */
export const addPlatformEntry = async (userId, platform, entryData) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get current data
    const currentData = await getUserSocialMediaData(userId);
    
    // Create new entry
    const newEntry = {
      id: Date.now().toString(),
      followers: Number(entryData.followers) || 0,
      views: Number(entryData.views) || 0,
      likes: Number(entryData.likes) || 0,
      comments: Number(entryData.comments) || 0,
      shares: Number(entryData.shares) || 0,
      month: String(entryData.month),
      year: Number(entryData.year),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Add entry to platform
    const updatedPlatformEntries = {
      ...currentData.platformEntries,
      [platform]: [...(currentData.platformEntries[platform] || []), newEntry]
    };

    // Update current social data with latest entry values
    const updatedSocialData = {
      ...currentData.socialData,
      [platform]: {
        ...currentData.socialData[platform],
        followers: newEntry.followers,
        views: newEntry.views,
        likes: newEntry.likes,
        comments: newEntry.comments,
        shares: newEntry.shares
      }
    };

    // Save updated data
    return await saveUserSocialMediaData(userId, updatedSocialData, updatedPlatformEntries);
  } catch (error) {
    console.error('Error adding platform entry:', error);
    throw error;
  }
};

/**
 * Update an existing platform entry for a specific user
 * @param {string} userId - The user's UID
 * @param {string} platform - Platform name (facebook, instagram, tiktok, youtube)
 * @param {string} entryId - Entry ID to update
 * @param {Object} entryData - Updated entry data object
 * @returns {Promise<Object>} Updated social media data
 */
export const updatePlatformEntry = async (userId, platform, entryId, entryData) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get current data
    const currentData = await getUserSocialMediaData(userId);
    
    // Update the specific entry
    const updatedPlatformEntries = {
      ...currentData.platformEntries,
      [platform]: currentData.platformEntries[platform].map(entry => 
        entry.id === entryId 
          ? {
              ...entry,
              followers: Number(entryData.followers) || 0,
              views: Number(entryData.views) || 0,
              likes: Number(entryData.likes) || 0,
              comments: Number(entryData.comments) || 0,
              shares: Number(entryData.shares) || 0,
              month: String(entryData.month),
              year: Number(entryData.year),
              updatedAt: new Date().toISOString()
            }
          : entry
      )
    };

    // Get the latest entry for this platform to update current social data
    const latestEntry = updatedPlatformEntries[platform]
      .sort((a, b) => {
        const yearDiff = b.year - a.year;
        if (yearDiff !== 0) return yearDiff;
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return months.indexOf(b.month) - months.indexOf(a.month);
      })[0];

    // Update current social data with latest entry values
    const updatedSocialData = {
      ...currentData.socialData,
      [platform]: {
        ...currentData.socialData[platform],
        followers: latestEntry ? latestEntry.followers : currentData.socialData[platform].followers,
        views: latestEntry ? latestEntry.views : currentData.socialData[platform].views,
        likes: latestEntry ? latestEntry.likes : currentData.socialData[platform].likes,
        comments: latestEntry ? latestEntry.comments : currentData.socialData[platform].comments,
        shares: latestEntry ? latestEntry.shares : currentData.socialData[platform].shares
      }
    };

    // Save updated data
    return await saveUserSocialMediaData(userId, updatedSocialData, updatedPlatformEntries);
  } catch (error) {
    console.error('Error updating platform entry:', error);
    throw error;
  }
};

/**
 * Delete a platform entry for a specific user
 * @param {string} userId - The user's UID
 * @param {string} platform - Platform name (facebook, instagram, tiktok, youtube)
 * @param {string} entryId - Entry ID to delete
 * @returns {Promise<Object>} Updated social media data
 */
export const deletePlatformEntry = async (userId, platform, entryId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get current data
    const currentData = await getUserSocialMediaData(userId);
    
    // Remove the specific entry
    const updatedPlatformEntries = {
      ...currentData.platformEntries,
      [platform]: currentData.platformEntries[platform].filter(entry => entry.id !== entryId)
    };

    // Get the latest remaining entry for this platform to update current social data
    const latestEntry = updatedPlatformEntries[platform]
      .sort((a, b) => {
        const yearDiff = b.year - a.year;
        if (yearDiff !== 0) return yearDiff;
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return months.indexOf(b.month) - months.indexOf(a.month);
      })[0];

    // Update current social data with latest entry values or reset to 0 if no entries
    const updatedSocialData = {
      ...currentData.socialData,
      [platform]: {
        ...currentData.socialData[platform],
        followers: latestEntry ? latestEntry.followers : 0,
        views: latestEntry ? latestEntry.views : 0,
        likes: latestEntry ? latestEntry.likes : 0,
        comments: latestEntry ? latestEntry.comments : 0,
        shares: latestEntry ? latestEntry.shares : 0
      }
    };

    // Save updated data
    return await saveUserSocialMediaData(userId, updatedSocialData, updatedPlatformEntries);
  } catch (error) {
    console.error('Error deleting platform entry:', error);
    throw error;
  }
};

/**
 * Save management request data for a specific user
 * @param {string} userId - The user's UID
 * @param {Object} managementRequestData - Management request data object
 * @returns {Promise<Object>} Updated social media data
 */
export const saveManagementRequest = async (userId, managementRequestData) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get user data for display name, email, and company
    const userDoc = await getDoc(doc(db, 'users', userId));
    let requestedByName = 'Unknown User';
    let userEmail = 'unknown@example.com';
    let companyName = 'Unknown Company';
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const fullName = userData.displayName || 
                      (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                      userData.email || 
                      'Unknown User';
      
      companyName = userData.company || 'Unknown Company';
      
      // Format as "Name" with "Company" underneath
      requestedByName = `${fullName}\n${companyName}`;
      userEmail = userData.email || 'unknown@example.com';
    } else {
      // If userId doesn't work, try to find user by email from auth
      if (auth.currentUser && auth.currentUser.email) {
        try {
          const usersQuery = query(
            collection(db, 'users'),
            where('email', '==', auth.currentUser.email)
          );
          const usersSnapshot = await getDocs(usersQuery);
          
          if (!usersSnapshot.empty) {
            const userData = usersSnapshot.docs[0].data();
            const fullName = userData.displayName || 
                            (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : '') ||
                            userData.email || 
                            'Unknown User';
            
            companyName = userData.company || 'Unknown Company';
            
            // Format as "Name" with "Company" underneath
            requestedByName = `${fullName}\n${companyName}`;
            userEmail = userData.email || 'unknown@example.com';
          }
        } catch (error) {
          console.error('Error finding user by email:', error);
        }
      }
    }

    // Get current data
    const currentData = await getUserSocialMediaData(userId);
    
    // Create management request object for social media data
    const managementRequest = {
      selectedPlatforms: Array.isArray(managementRequestData.selectedPlatforms) ? managementRequestData.selectedPlatforms : [],
      feedPostsPerWeek: Number(managementRequestData.feedPostsPerWeek) || 0,
      storyPostsPerWeek: Number(managementRequestData.storyPostsPerWeek) || 0,
      shortsVideoPostsPerWeek: Number(managementRequestData.shortsVideoPostsPerWeek) || 0,
      submittedAt: new Date().toISOString(),
      status: 'pending'
    };

    // Create management request object for marketingRequests collection
    const marketingRequest = {
      userId: userId,
      requestedByName: requestedByName,
      userEmail: userEmail,
      title: 'Social Media Management Request',
      description: `Social media management request for ${managementRequestData.selectedPlatforms?.join(', ') || 'platforms'}`,
      category: 'social',
      priority: 'normal',
      budget: 'Not specified',
      status: 'pending',
      platforms: managementRequestData.selectedPlatforms || [],
      feedPosts: managementRequestData.feedPostsPerWeek || 0,
      storyPosts: managementRequestData.storyPostsPerWeek || 0,
      videoPosts: managementRequestData.shortsVideoPostsPerWeek || 0,
      selectedPlatforms: managementRequestData.selectedPlatforms || [],
      feedPostsPerWeek: managementRequestData.feedPostsPerWeek || '',
      storyPostsPerWeek: managementRequestData.storyPostsPerWeek || '',
      shortsVideoPostsPerWeek: managementRequestData.shortsVideoPostsPerWeek || '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Update the social media data with management request
    const updatedData = {
      ...currentData,
      managementRequest: managementRequest
    };

    // Save to social media database
    const userDocRef = doc(db, SOCIAL_MEDIA_COLLECTION, userId);
    await setDoc(userDocRef, {
      socialData: updatedData.socialData,
      platformEntries: updatedData.platformEntries,
      managementRequest: managementRequest,
      updatedAt: serverTimestamp(),
      createdAt: currentData.createdAt || serverTimestamp()
    });

    // Save to marketingRequests collection
    const requestsRef = collection(db, 'marketingRequests');
    const docRef = await addDoc(requestsRef, marketingRequest);
    
    console.log('✅ Social media management request saved:', docRef.id);

    // Trigger marketing fulfillment notification for social media management request
    try {
      const { triggerFulfillmentNotification } = await import('./notificationTriggerService');
      await triggerFulfillmentNotification('marketing', {
        type: 'social media management',
        name: 'Social Media Management Request',
        description: `Social media management request for ${managementRequestData.selectedPlatforms?.join(', ') || 'platforms'}`,
        requestedBy: requestedByName,
        company: 'User Company', // You might want to get this from user data
        platforms: managementRequestData.selectedPlatforms || [],
        feedPostsPerWeek: managementRequestData.feedPostsPerWeek || 0,
        storyPostsPerWeek: managementRequestData.storyPostsPerWeek || 0,
        shortsVideoPostsPerWeek: managementRequestData.shortsVideoPostsPerWeek || 0,
        timestamp: new Date().toISOString()
      });
      console.log('✅ Marketing fulfillment notification sent for social media management request');
    } catch (notificationError) {
      console.error('❌ Error sending marketing fulfillment notification:', notificationError);
      // Don't fail the request if notification fails
    }

    return updatedData;
  } catch (error) {
    console.error('Error saving management request:', error);
    throw error;
  }
};

/**
 * Cancel management request for a specific user
 * @param {string} userId - The user's UID
 * @returns {Promise<Object>} Updated social media data
 */
export const cancelManagementRequest = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }

    const auth = getAuth();
    if (!auth.currentUser || auth.currentUser.uid !== userId) {
      throw new Error('User must be authenticated and can only save their own data');
    }

    // Get current data
    const currentData = await getUserSocialMediaData(userId);
    
    // Remove management request
    const updatedData = {
      ...currentData,
      managementRequest: null
    };

    // Save to social media database
    const userDocRef = doc(db, SOCIAL_MEDIA_COLLECTION, userId);
    await setDoc(userDocRef, {
      socialData: updatedData.socialData,
      platformEntries: updatedData.platformEntries,
      managementRequest: null,
      updatedAt: serverTimestamp(),
      createdAt: currentData.createdAt || serverTimestamp()
    });

    // Remove from marketingRequests collection
    try {
      const requestsQuery = query(
        collection(db, 'marketingRequests'),
        where('userId', '==', userId),
        where('category', '==', 'social'),
        where('status', '==', 'pending')
      );
      const requestsSnapshot = await getDocs(requestsQuery);
      
      // Delete all pending social media requests for this user
      const deletePromises = requestsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      
      console.log('✅ Cancelled social media management requests:', requestsSnapshot.docs.length);
    } catch (error) {
      console.error('Error removing from marketingRequests:', error);
      // Don't throw here, as the main operation succeeded
    }

    return updatedData;
  } catch (error) {
    console.error('Error canceling management request:', error);
    throw error;
  }
};